/**
 * Workspace files mentioned in the agent's text: what counts as a reference,
 * and how it reaches the renderer. No React here, so it can be tested alone.
 *
 * The agent is told to link files as `[name](ws://path)`. Two looser forms are
 * picked up too, because a model does not always follow that: a bare `ws://path`
 * in the text, and a path in inline code. The inline-code form only becomes a
 * link once the workspace listing confirms the file (`resolveKnownPath`): plenty
 * of inline code looks like a path without being a file in this workspace.
 */
import { defaultUrlTransform, type UrlTransform } from 'react-markdown';

export type FileKind = 'image' | 'video' | 'audio' | 'html' | 'other';

const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i;
const VIDEO_EXTS = /\.(mp4|webm|mov|avi|mkv|m4v)$/i;
const AUDIO_EXTS = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
const HTML_EXTS = /\.html?$/i;

export function fileKind(name: string, contentType?: string): FileKind {
  const ct = contentType ?? '';
  if (HTML_EXTS.test(name)) return 'html';
  if (ct.startsWith('image/') || IMAGE_EXTS.test(name)) return 'image';
  if (ct.startsWith('video/') || VIDEO_EXTS.test(name)) return 'video';
  if (ct.startsWith('audio/') || AUDIO_EXTS.test(name)) return 'audio';
  return 'other';
}

export function fileName(path: string): string {
  return path.split('/').filter(Boolean).pop() ?? path;
}

/** The agent's own namespaces under `ws://`: readable by it, but nothing in the workspace to open. */
const VIRTUAL_ROOTS = /^\.(skills|history)(\/|$)/;

const WS_SCHEME = /^ws:\/\//i;

export function isWorkspaceUrl(url: string | undefined | null): url is string {
  return !!url && WS_SCHEME.test(url);
}

/** `ws://a/b.png`, `/workspace/a/b.png` or `./a/b.png` → `a/b.png`; null when it names nothing to open. */
export function toWorkspaceRelPath(ref: string): string | null {
  let path = ref.trim().replace(WS_SCHEME, '').replace(/^\/workspace\//, '').replace(/^\.\//, '');
  // A link's path arrives percent-encoded (a space is %20) and may point at a line.
  path = path.replace(/#.*$/, '');
  try { path = decodeURIComponent(path); } catch { /* a literal % in the name */ }
  if (!path || path.startsWith('/') || VIRTUAL_ROOTS.test(path)) return null;
  return path;
}

/** react-markdown drops every scheme it doesn't know, `ws:` included. */
export const fileRefUrlTransform: UrlTransform = (url) => (isWorkspaceUrl(url) ? url : defaultUrlTransform(url));

/** Inline code that could be a file: ending in an extension, or under /workspace/. A name may hold spaces. */
function looksLikePath(text: string): boolean {
  if (text.length > 240 || text.includes('\n') || text.includes('://')) return false;
  return text.startsWith('/workspace/') || /\.[A-Za-z0-9]{1,6}$/.test(text);
}

/**
 * The file a path in inline code means, from the workspace's file paths: the
 * exact path, or else the one file it is the tail of. An ambiguous tail
 * (`index.html` with two of them) opens nothing rather than the wrong file.
 */
export function resolveKnownPath(candidate: string, known: readonly string[]): string | null {
  const path = toWorkspaceRelPath(candidate);
  if (!path) return null;
  if (known.includes(path)) return path;
  const tails = known.filter((k) => k.endsWith(`/${path}`));
  return tails.length === 1 ? tails[0]! : null;
}

/** The element `rehypeFileRefs` leaves for inline code; AiChat maps it to a component. */
export const FILE_REF_TAG = 'wsfile';

const BARE_WS_URL = /ws:\/\/[^\s<>()[\]`'"]+/gi;

/** Split a text node around its bare `ws://` URLs; null when it has none worth linking. */
function linkBareUrls(value: string): any[] | null {
  const out: any[] = [];
  let last = 0;
  for (const match of value.matchAll(BARE_WS_URL)) {
    const url = match[0].replace(/[.,:;!?]+$/, ''); // the sentence's punctuation, not the path's
    const path = toWorkspaceRelPath(url);
    if (!path) continue;
    const at = match.index;
    if (at > last) out.push({ type: 'text', value: value.slice(last, at) });
    out.push({
      type: 'element',
      tagName: 'a',
      properties: { href: url },
      children: [{ type: 'text', value: fileName(path) }],
    });
    last = at + url.length;
  }
  if (out.length === 0) return null;
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
  return out;
}

const textOf = (node: any): string =>
  node.type === 'text' ? node.value : (node.children ?? []).map(textOf).join('');

/**
 * Rehype plugin: bare `ws://` URLs become links, and inline code that may name a
 * file becomes a `wsfile` element. A new element rather than a `code` override
 * because MarkdownPre finds its block's language by `child.type === 'code'`.
 * Code blocks and existing links are left alone.
 */
export function rehypeFileRefs() {
  return (tree: any) => {
    const visit = (parent: any) => {
      const kids = parent.children;
      if (!Array.isArray(kids)) return;
      for (let i = 0; i < kids.length; i++) {
        const node = kids[i];
        if (node.type === 'text') {
          const split = linkBareUrls(node.value);
          if (split) {
            kids.splice(i, 1, ...split);
            i += split.length - 1;
          }
          continue;
        }
        if (node.type !== 'element' || node.tagName === 'pre' || node.tagName === 'a') continue;
        if (node.tagName === 'code') {
          const text = textOf(node).trim();
          if (isWorkspaceUrl(text) ? toWorkspaceRelPath(text) : looksLikePath(text)) {
            kids[i] = { type: 'element', tagName: FILE_REF_TAG, properties: { dataRef: text }, children: node.children };
          }
          continue;
        }
        visit(node);
      }
    };
    visit(tree);
  };
}
